import { ViewIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Skeleton,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
  VStack,
  Wrap,
  WrapItem
} from "@chakra-ui/react";
import { ChainId, shortenAddress, useEtherBalance, useEthers } from "@usedapp/core";
import axios from "axios";
import { startOfDay } from "date-fns";
import { isSameDay } from "date-fns/esm";
import { utils } from "ethers";
import { minBy, sumBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AppContent } from "../../components/AppContent";
import { ErrorModal } from "../../components/ErrorModal";
import { SharedButton } from "../../components/SharedButton";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import { images } from "../../core/constants/images";
import { MINT_TYPE } from "../../core/constants/nft";
import { PAYMENT_STRATEGY } from "../../core/constants/paymentStrategy";
import { LAYER_1_TOKENS } from "../../core/constants/token";
import { useERC20Balance } from "../../core/hooks/useERC20Balance";
import { useRoyaltyInfo } from "../../core/hooks/useRoyaltyInfo";
import { useCreditStatus } from "../../core/hooks/useStatus";
import { ICollection } from "../../core/models/collection";
import { IMoralisNft, INftActivity, INftSale } from "../../core/models/nft";
import { Quote } from "../../core/models/quote";
import { IUser } from "../../core/models/user";
import { uriToHttps } from "../../core/utils/ipfs";
import { marketplaceService } from "../../services/marketplace.service";
import { nftService } from "../../services/nft.service";
import { quoteService } from "../../services/quote.service";
import { userService } from "../../services/user.service";
import { useAppSelector } from "../../store/hook";
import { ActivitiesInfo } from "./components/ActivitiesInfo";
import { AssetPrimaryInfo } from "./components/AssetPrimaryInfo";
import { AssetSecondaryInfo } from "./components/AssetSecondaryInfo";
import { CancelListingForm } from "./components/CancelListingForm";
import { CompletePaymentModal } from "./components/CompletePaymentModal";
import { CreditStatusWarningModal } from "./components/CreditStatusWarningModal";
import { FavoriteNftDetail } from "./components/FavoriteNftDetail";
import { ListingForm } from "./components/ListingForm";
import { OwnersTab } from "./components/OwnersTab";
import { PurchaseForm } from "./components/PurchaseForm";
import { PurchaseProcessModal } from "./components/PurchaseProcessModal";
import { PurchaseSuccessModal } from "./components/PurchaseSuccessModal";
import { SaleHistoryChart } from "./components/SaleHistoryChart";
import { ISaleStatisticPerDay } from "./models/sale";

enum PURCHASE_STATE {
  NONE,
  COMFIRMING,
  PURCHASING,
  SUCCESS,
  NOT_VERIFIED,
  INSUFFICIENT_BALANCE,
  LISTED_ERROR,
}

const AssetDetail = React.memo(() => {
  const { tokenAddress } = useParams<"tokenAddress">();
  const { tokenId } = useParams<"tokenId">();
  const { account, chainId, library } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);
  const navigate = useNavigate();

  const royaltyInfo = useRoyaltyInfo(tokenAddress, tokenId);

  const isClaimedBuyer = useCreditStatus(account);

  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  // Number of edition on sale
  const [notes, setNotes] = useState<string>("");
  const [selectedQuote, setSelectedQuote] = useState<Quote>();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [owners, setOwners] = useState<{ user: IUser; quantityOwned: string }[]>([]);
  const [royalty, setRoyalty] = useState<{ user: IUser; value: number }>();
  const [activities, setActivities] = useState<INftActivity[]>([]);
  const [priceHistory, setPriceHistory] = useState<ISaleStatisticPerDay[]>([]);

  const [isFetchingSales, setIsFetchingSales] = useState<boolean>(false);
  const [collection, setCollection] = useState<ICollection | null>();
  const [views, setViews] = useState(0);

  const [nft, setNft] = useState<IMoralisNft>();

  const isOwner = useMemo(
    () => !!account && owners.some((owner) => owner.user.id?.toLowerCase() === account.toLowerCase()),
    [owners, account, selectedQuote?.maker]
  );

  const token = useMemo(
    () => (selectedQuote ? supportedTokens.byId[selectedQuote.take.asset_type.token_address] : null),
    [selectedQuote?.take.asset_type.token_address, supportedTokens.byId]
  );
  const price = useMemo(
    () => (!!selectedQuote?.take?.value && token ? selectedQuote?.take.value / Math.pow(10, token.decimals) : 0),
    [selectedQuote?.take.value, token]
  );

  const userUnderlyingTokenBalance = useERC20Balance(
    token?.underlying && !LAYER_1_TOKENS.includes(token?.underlying) ? token?.underlying : null
  );
  const ethBalanceInBigNumber = useEtherBalance(account);
  const ethBalance = useMemo(
    () => (ethBalanceInBigNumber ? +utils.formatEther(ethBalanceInBigNumber) : 0),
    [ethBalanceInBigNumber]
  );
  const walletBalance = useMemo(
    () => (token?.underlying && LAYER_1_TOKENS.includes(token?.underlying) ? ethBalance : userUnderlyingTokenBalance),
    [token?.underlying, ethBalance, userUnderlyingTokenBalance]
  );

  const [purchaseState, setPurchaseState] = useState<PURCHASE_STATE>(PURCHASE_STATE.NONE);
  const [paymentStrategy, setPaymentStrategy] = useState<PAYMENT_STRATEGY | null>(null);

  useEffect(() => {
    const quoteByUser = quotes.find((i) => i.maker.toLowerCase() === account?.toLowerCase());

    if (quoteByUser) {
      setSelectedQuote(quoteByUser);
    } else if (!isOwner) {
      const cheapest = minBy<Quote>(quotes, "make.value");
      setSelectedQuote(cheapest);
    } else {
      setSelectedQuote(undefined);
    }
  }, [account, quotes, isOwner]);

  const getNftDetail = async () => {
    if (!tokenAddress || !tokenId) return;

    setIsLoading(true);
    const response = await nftService.getNftDetail(tokenAddress, tokenId);
    const uri = response.data.token_uri ? uriToHttps(response.data.token_uri) : undefined;

    const metadataResponse = uri ? await axios.get(uri) : undefined;
    setQuotes(response.data.quotes || []);
    setNft({
      token_address: response.data.token_address,
      token_id: response.data.token_id,
      metadata: metadataResponse
        ? metadataResponse.data
        : {
          image: response.data.image,
          attributes: response.data.attributes,
          description: response.data.description,
          name: response.data.name,
        },
      name: response.data.token_name,
      token_uri: response.data.token_uri,
      contract_type: response.data.contract_type,
      amount: response.data.value?.toString(),
      mint_type: response.data.mint_type,
      creators: response.data.creators,
      royalties: response.data.royalties,
      signatures: response.data.signatures,
    });
    setCollection(response.data.collection);
    setOwners(response.data.owners || []);

    if (
      response.data.mint_type === MINT_TYPE.LAZY &&
      response.data?.royalties &&
      response.data?.royalties?.length > 0
    ) {
      const royalty = response.data?.royalties[0];
      const creatorResponse = await userService.getProfileById(royalty.account);
      setRoyalty({ user: creatorResponse.data, value: royalty.value / 100 });
    }

    setIsLoading(false);
  };

  const getActivities = async () => {
    if (!tokenAddress || !tokenId) return;
    const response = await nftService.getActivities(tokenAddress, tokenId);

    setActivities(response.data || []);
  };

  const getSales = async () => {
    if (!tokenAddress || !tokenId) return;
    setIsFetchingSales(true);
    const response = await nftService.getSales(tokenAddress, tokenId);

    setPriceHistory(
      (response.data || []).reduce((acc: ISaleStatisticPerDay[], cur: INftSale, index: number, sales: INftSale[]) => {
        if (acc.some((i) => isSameDay(i.date, startOfDay(cur.timestamp))) || !cur.timestamp) {
          return acc;
        }

        const salesInDay = sales.filter((s) => isSameDay(startOfDay(s.timestamp), startOfDay(cur.timestamp)));
        return [
          ...acc,
          {
            date: startOfDay(cur.timestamp),
            price: sumBy(salesInDay, "priceInUSD") / salesInDay.length,
            noOfSales: salesInDay.length,
          },
        ];
      }, [])
    );
    setIsFetchingSales(false);
  };

  const getViews = async (
    tokenAddress: string | undefined,
    tokenId: string | undefined,
    account: string | null | undefined,
    chainId?: ChainId
  ) => {
    if (tokenAddress && tokenId) {
      const response = await marketplaceService.getViews(tokenAddress, tokenId, account, chainId);
      setViews(response.data || 0);
    }
  };

  useEffect(() => {
    getNftDetail();
    getActivities();
    getViews(tokenAddress, tokenId, account, chainId);
    getSales();
  }, []);

  useEffect(() => {
    async function getCreator() {
      if (!royaltyInfo?.receiver) return;
      const creatorResponse = await userService.getProfileById(royaltyInfo.receiver);
      setRoyalty({ user: creatorResponse.data, value: royaltyInfo?.royaltyPercent });
    }

    getCreator();
  }, [royaltyInfo?.receiver]);

  const onPurchaseSuccess = async () => {
    if (!selectedQuote?.id) return;
    await quoteService.delist(selectedQuote?.id);
    await getNftDetail();
    setPurchaseState(PURCHASE_STATE.SUCCESS);
  };

  const onListingSuccess = () => {
    getNftDetail();
  };

  const onDelistSuccess = () => {
    getNftDetail();
  };

  const validateStateBeforePurchasing = async (quote: Quote) => {
    // const itemOnChain = await marketplaceContract?.getListingInfo(nft?.token_address, nft?.token_id, nft?.owner_of);

    // if (itemOnChain.seller === ethers.constants.AddressZero) {
    //   setPurchaseState(PURCHASE_STATE.LISTED_ERROR);
    //   sync();

    //   return false;
    // }

    return true;
  };

  const onPayInFour = async (quote: Quote) => {
    const isValid = await validateStateBeforePurchasing(quote);
    if (!isValid) return;

    if (isClaimedBuyer) {
      setPurchaseState(PURCHASE_STATE.COMFIRMING);
      setPaymentStrategy(PAYMENT_STRATEGY.IN_FOUR);
    } else {
      setPurchaseState(PURCHASE_STATE.NOT_VERIFIED);
    }
  };

  const onPayFull = async (quote: Quote) => {
    const isValid = await validateStateBeforePurchasing(quote);
    if (!isValid) return;

    setPurchaseState(PURCHASE_STATE.COMFIRMING);
    setPaymentStrategy(PAYMENT_STRATEGY.FULL);
  };

  const onComfirmPayment = () => {
    if (!token) return;
    const priceMantissa = (price || 0) * Math.pow(10, token.decimals);
    const principal = Math.ceil(priceMantissa / 4);
    const walletBalanceMantissa = (walletBalance || 0) * Math.pow(10, token.decimals);

    if (paymentStrategy === PAYMENT_STRATEGY.IN_FOUR && principal > walletBalanceMantissa) {
      setPurchaseState(PURCHASE_STATE.INSUFFICIENT_BALANCE);
    } else if (paymentStrategy === PAYMENT_STRATEGY.FULL && priceMantissa > walletBalanceMantissa) {
      setPurchaseState(PURCHASE_STATE.INSUFFICIENT_BALANCE);
    } else {
      setPurchaseState(PURCHASE_STATE.PURCHASING);
    }
  };

  const renderForm = () => {
    const isMyQuote = selectedQuote?.maker.toLowerCase() === account?.toLowerCase();

    if (isMyQuote && isOwner && selectedQuote) {
      return <CancelListingForm nft={nft} quote={selectedQuote} onSuccess={onDelistSuccess} />;
    } else if (!isMyQuote && selectedQuote) {
      return <PurchaseForm nft={nft} quote={selectedQuote} onPayInFour={onPayInFour} onPayFull={onPayFull} />;
    } else if (isOwner) {
      return <ListingForm nft={nft} onSuccess={onListingSuccess} owners={owners} />;
    }
  };

  const onPhoneLayout = useBreakpointValue({ "base": true, "md": false });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <AppContent maxWidth="1100px">
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1.3fr" }} gridGap="40px">
          {/* NFT Box */}
          <Box>
            <Skeleton isLoaded={!isLoading}>
              <AssetPrimaryInfo
                nft={nft}
                notes={notes}
                setNotes={setNotes}
                disablesNotes={Boolean(selectedQuote)}
                isOwner={isOwner}
                maxMediaHeight={onPhoneLayout ? "240px" : "400px"}
              />
            </Skeleton>
            <Skeleton
              display={{ base: 'none', md: 'initial' }}
              isLoaded={!isLoading}>
              <Divider border={1} />
              <Box my={6}>
                <AssetSecondaryInfo
                  nft={nft}
                  notes={notes}
                  setNotes={setNotes}
                  disablesNotes={Boolean(selectedQuote)}
                  isOwner={isOwner}
                />
              </Box>
            </Skeleton>
          </Box>

          {/* Detail Box */}
          <Box>
            <Box>
              <Skeleton isLoaded={!isLoading}>
                <Flex mt={onPhoneLayout ? -8 : 0} justifyContent={"space-between"}>
                  <Heading size="lg" color="#14233D">
                    {nft?.metadata?.name}
                  </Heading>
                  <SharedButton content={`Checkout ${nft?.metadata?.name} collectible on @bePAY`} />
                </Flex>
              </Skeleton>

              <Skeleton isLoaded={!isLoading}>
                <Flex marginTop={2} marginBottom={6}>
                  <Wrap spacing={"50px"}>
                    <WrapItem>
                      <Stack>
                        <HStack>
                          <ViewIcon />
                          <Text fontSize={"sm"} fontWeight={500}>
                            {t("view", { count: views })}
                          </Text>
                        </HStack>
                      </Stack>
                    </WrapItem>
                    <WrapItem>
                      <FavoriteNftDetail tokenAddress={tokenAddress} tokenId={tokenId} address={account} />
                    </WrapItem>
                  </Wrap>
                </Flex>
              </Skeleton>

              <Skeleton isLoaded={!isLoading}>
                <Flex marginTop={6}>
                  {!!royalty && (
                    <Stack flex={1}>
                      <Text fontSize={"sm"} color={"gray.500"} fontWeight={500}>
                        {t("Creator")}{" "}
                        <Text as="span">
                          ({royalty.value}% {t("royalties")})
                        </Text>
                      </Text>
                      <HStack cursor={"pointer"} onClick={() => navigate(`/profile/assets/${royalty?.user?.id}`)}>
                        <Avatar src={royalty?.user?.image || ""} size={"sm"} />
                        <Text fontSize={"md"} fontWeight={500}>
                          {royalty?.user?.display_name || (royalty?.user?.id && shortenAddress(royalty.user.id))}
                        </Text>
                      </HStack>
                    </Stack>
                  )}
                  <Stack flex={1}>
                    <Text fontSize={"sm"} color={"gray.500"} fontWeight={500}>
                      {t("Collection")}
                    </Text>
                    <HStack cursor={"pointer"} onClick={() => navigate(`/collections/${tokenAddress}`)}>
                      <Image
                        borderRadius={"full"}
                        src={collection?.logo || ""}
                        objectFit="cover"
                        boxSize={"32px"}
                        fallbackSrc={images.collectionLogoDefault}
                      />
                      <Text fontSize={"md"} fontWeight={500}>
                        {collection?.name}
                      </Text>
                    </HStack>
                  </Stack>
                </Flex>
              </Skeleton>
            </Box>

            <Box padding="24px 0px">
              <Divider />
            </Box>
            
            <Skeleton isLoaded={!isLoading}>
              {renderForm()}
            </Skeleton>

            <Skeleton isLoaded={!isLoading} display={{ base: 'initial', md: 'none' }}>
              <Divider my={6} border={1} />
              <Box my={6}>
                <AssetSecondaryInfo
                  nft={nft}
                  notes={notes}
                  setNotes={setNotes}
                  disablesNotes={Boolean(selectedQuote)}
                  isOwner={isOwner}
                />
              </Box>
            </Skeleton>

            <Skeleton isLoaded={!isLoading}>
              <Tabs mt={8} variant="enclosed" colorScheme="blue">
                <TabList>
                  <Tab fontWeight={700}>{t("Owners")}</Tab>
                  <Tab fontWeight={700}>{t("Provenance")}</Tab>
                  <Tab fontWeight={700}>{t("Price history")}</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <OwnersTab
                      owners={owners}
                      quotes={quotes}
                      selectedQuote={selectedQuote}
                      nft={nft}
                      onPayFull={(quote) => {
                        setSelectedQuote(quote);
                        onPayFull(quote);
                      }}
                      onPayInFour={(quote) => {
                        setSelectedQuote(quote);
                        onPayInFour(quote);
                      }}
                    />
                  </TabPanel>
                  <TabPanel>
                    <ActivitiesInfo activities={activities} />
                  </TabPanel>
                  <TabPanel>
                    <SaleHistoryChart data={priceHistory} isLoading={isFetchingSales} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Skeleton>
          </Box>
        </Grid>
      </AppContent>
      {!!nft && !!selectedQuote && (
        <CompletePaymentModal
          isVisible={purchaseState === PURCHASE_STATE.COMFIRMING}
          onClose={() => {
            setPurchaseState(PURCHASE_STATE.NONE);
          }}
          nft={nft}
          quote={selectedQuote}
          onConfirm={onComfirmPayment}
          paymentStrategy={paymentStrategy}
        />
      )}
      {!!selectedQuote && nft && token && (
        <PurchaseProcessModal
          isVisible={purchaseState === PURCHASE_STATE.PURCHASING}
          onClose={() => {
            setPurchaseState(PURCHASE_STATE.NONE);
          }}
          nft={nft}
          quote={selectedQuote}
          token={token}
          paymentStrategy={paymentStrategy}
          onSuccess={onPurchaseSuccess}
          price={+(price || "0")}
        />
      )}
      {!!nft && (
        <PurchaseSuccessModal
          isVisible={purchaseState === PURCHASE_STATE.SUCCESS}
          onClose={() => {
            setPurchaseState(PURCHASE_STATE.NONE);
          }}
          nft={nft}
        />
      )}
      <CreditStatusWarningModal
        isVisible={purchaseState === PURCHASE_STATE.NOT_VERIFIED}
        onClose={() => {
          setPurchaseState(PURCHASE_STATE.NONE);
        }}
      />
      <ErrorModal
        isVisible={purchaseState === PURCHASE_STATE.INSUFFICIENT_BALANCE}
        onClose={() => {
          setPurchaseState(PURCHASE_STATE.NONE);
        }}
        message={t("Insufficient funds")}
      />
      <ErrorModal
        isVisible={purchaseState === PURCHASE_STATE.LISTED_ERROR}
        onClose={() => {
          setPurchaseState(PURCHASE_STATE.NONE);
        }}
        message={t("Not found. This item is not listed!")}
      />
    </>
  );
});

export default AssetDetail;
